import { useState, useContext, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { toast } from "react-toastify";

import LocationTeacherOverview from "./LocationTeacherOverview";
import SchoolReportCardStatistics from "./SchoolReportCardStatistics";
import SemesterConnectedPanel from "./SemesterConnectedPanel";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import EprOverviewDao from "../../dao/EprOverviewDao";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";

import { GlobalContext } from "../contextProvider/ContextProvider.jsx";
import { hasRole } from "../../utils/isUser";
import useOverviewSchoolYear from "../../hooks/useOverviewSchoolYear";
import { formatSchoolYearName, shouldDisableSecondSemesterTab, buildOverviewSyParam } from "./overviewSchoolYear";
import { computeLocationRollup } from "../../utils/teacherOverview/LocationTeacherOverviewUtils";
import { resolveCurrentYearStatsSemester } from "../../utils/teacherOverview/resolveCurrentYearStatsSemester";
import { getEpoch, getTodayWithTime } from "../../utils/DateFormatter";

import * as teacherOverviewStyles from "../../styles/TeacherOverviewHome.module.scss";

/**
 * This is the Epr Overview page for admins to view teacher's progress with their EPRs.
 * @name TeacherOverviewHome
 * @returns {JSX.Element}
 */

const adminLocations = [];

const teachersCacheKey = (locationKey, schoolYearKey) =>
    `${locationKey}|${schoolYearKey != null ? schoolYearKey : "current"}`;

const TeacherOverviewHome = ({ setLabelObj, setSelectedLink, locationKey }) => {
    const { state: globalState } = useContext(GlobalContext);
    const {
        currentTimePeriodDto,
        viewTimePeriodDto,
        userDetails,
        allLocationDtos,
        legacyLocationDtos,
        token,
        actionWindowsDto,
        timePeriodDto,
        schoolYearDto
    } = globalState || {};
    const { years, selectedKey, setSelectedKey, activeKey, options } = useOverviewSchoolYear();

    const [viewingSemester, setViewingSemester] = useState();
    const [teacherOverviewData, setTeacherOverviewData] = useState([]);
    const [teachersLoader, setTeachersLoader] = useState(true);
    const [viewedSchoolActionWindows, setViewedSchoolActionWindows] = useState(null);
    const teachersCacheRef = useRef({});

    const selectedSemesterName =
        viewingSemester || currentTimePeriodDto?.name || viewTimePeriodDto?.name || "1st Semester";
    const currentPeriodName = currentTimePeriodDto?.name || viewTimePeriodDto?.name;
    const disableSecondTab = shouldDisableSecondSemesterTab({
        selectedKey,
        activeKey,
        currentPeriodName
    });

    const sy = buildOverviewSyParam({ selectedKey, activeKey });
    const isHistoricalYear = sy != null;

    useEffect(() => {
        if (disableSecondTab && selectedSemesterName === "2nd Semester") {
            setViewingSemester("1st Semester");
        }
    }, [disableSecondTab, selectedSemesterName]);

    const derivedLocationKey =
        locationKey || userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey;

    const isEprAdmin = hasRole(userDetails, "EPR_ADMIN");
    const usesViewedSchoolActionWindows = Boolean(hasRole(userDetails, ["EPR_ADMIN", "PK12"]));

    const locationName = useMemo(() => {
        if (!derivedLocationKey) return "";
        const matchKey = (loc) => String(loc.key) === String(derivedLocationKey);
        const location = allLocationDtos?.find(matchKey) || legacyLocationDtos?.find(matchKey);
        return location?.name || `Unknown School (${derivedLocationKey})`;
    }, [derivedLocationKey, allLocationDtos, legacyLocationDtos]);
    const selectedSchoolYear = years.find((year) => String(year.key) === String(selectedKey));
    const schoolYearName = formatSchoolYearName(selectedSchoolYear?.name);
    const pageTitle = `Report Card Overview${schoolYearName ? ` (${schoolYearName})` : ""}`;

    useEffect(() => {
        if (!derivedLocationKey || !token) {
            setTeacherOverviewData([]);
            setTeachersLoader(false);
            return undefined;
        }

        const cacheKey = teachersCacheKey(derivedLocationKey, sy);
        const cached = teachersCacheRef.current[cacheKey];
        if (cached) {
            setTeacherOverviewData(cached);
            setTeachersLoader(false);
            return undefined;
        }

        let cancelled = false;
        setTeachersLoader(true);

        const fetchTeachers = async () => {
            try {
                const overviewResponse = await EprOverviewDao({
                    action: "teachersByLocationRead",
                    locationKey: derivedLocationKey,
                    token,
                    ...(sy != null ? { schoolYearKey: sy } : {})
                });

                if (cancelled) return;

                const payload = overviewResponse?.data?.payload || [];
                teachersCacheRef.current[cacheKey] = payload;
                setTeacherOverviewData(payload);
            } catch (error) {
                if (cancelled) return;
                console.error(error);
                toast.error("Something went wrong fetching teacher information.");
                setTeacherOverviewData([]);
            } finally {
                if (!cancelled) setTeachersLoader(false);
            }
        };

        fetchTeachers();
        return () => {
            cancelled = true;
        };
    }, [derivedLocationKey, token, sy]);

    useEffect(() => {
        if (isHistoricalYear || !usesViewedSchoolActionWindows) {
            setViewedSchoolActionWindows(null);
            return undefined;
        }
        if (!derivedLocationKey || !token || schoolYearDto?.key == null) {
            setViewedSchoolActionWindows(null);
            return undefined;
        }

        let cancelled = false;
        setViewedSchoolActionWindows(null);

        ElementaryProgressReportDao({
            action: "actionWindowsRead",
            locationKey: derivedLocationKey,
            schoolYearKey: schoolYearDto.key,
            token
        })
            .then((response) => {
                if (cancelled) return;
                setViewedSchoolActionWindows(response?.data?.payload || []);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setViewedSchoolActionWindows([]);
            });

        return () => {
            cancelled = true;
        };
    }, [isHistoricalYear, usesViewedSchoolActionWindows, derivedLocationKey, token, schoolYearDto?.key]);

    const effectiveActionWindows = usesViewedSchoolActionWindows ? viewedSchoolActionWindows : actionWindowsDto;

    const currentYearStatsSemester = useMemo(() => {
        if (isHistoricalYear || effectiveActionWindows == null) return null;
        return resolveCurrentYearStatsSemester({
            actionWindows: effectiveActionWindows,
            timePeriodDto,
            currentPeriodName,
            nowEpoch: getEpoch(getTodayWithTime())
        });
    }, [isHistoricalYear, effectiveActionWindows, timePeriodDto, currentPeriodName]);

    // Past 2nd GRADING_PROFILE close, follow the semester tab like historical years.
    // While only 1st is unlocked (2nd window still open), hide stats on the 2nd tab.
    const statsFollowSemesterTab = isHistoricalYear || currentYearStatsSemester === "2nd Semester";
    const rollupSemesterName = statsFollowSemesterTab ? selectedSemesterName : currentYearStatsSemester;
    const showStatistics =
        isHistoricalYear ||
        (currentYearStatsSemester != null &&
            (statsFollowSemesterTab || selectedSemesterName === currentYearStatsSemester));

    const rollup = useMemo(
        () =>
            showStatistics && !teachersLoader && rollupSemesterName
                ? computeLocationRollup(teacherOverviewData, rollupSemesterName)
                : null,
        [showStatistics, teachersLoader, teacherOverviewData, rollupSemesterName]
    );

    const handleSchoolYearChange = (e) => {
        const next = e.target.value;
        setSelectedKey(Number.isNaN(Number(next)) ? next : Number(next));
    };

    const locationOverviewProps = {
        selectedSemesterName,
        schoolYearKey: selectedKey,
        activeSchoolYearKey: activeKey,
        setLabelObj,
        setSelectedLink,
        teacherOverviewData,
        teachersLoader
    };

    const renderLocationOverview = (embedded = false) => {
        if (isEprAdmin && !locationKey && adminLocations.length > 0) {
            return (
                <Accordion defaultActiveKey={adminLocations[0]}>
                    {adminLocations.map((key) => (
                        <Accordion.Item eventKey={key} key={key}>
                            <Accordion.Header>{locationName}</Accordion.Header>
                            <Accordion.Body>
                                <LocationTeacherOverview
                                    locationObj={{ locationKey: key, locationName: locationName }}
                                    embedded={embedded}
                                    {...locationOverviewProps}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            );
        }

        return (
            <LocationTeacherOverview
                locationObj={{ locationKey: derivedLocationKey, locationName: locationName }}
                embedded={embedded}
                {...locationOverviewProps}
            />
        );
    };

    return (
        <div className="template-wrapper teacher-overview-layout">
            <div className={teacherOverviewStyles.titleSection}>
                <h4 className="page-title">{pageTitle}</h4>
            </div>
            <SemesterConnectedPanel
                options={options}
                selectedKey={selectedKey}
                onSchoolYearChange={handleSchoolYearChange}
                selectedSemesterName={selectedSemesterName}
                onSemesterChange={setViewingSemester}
                disableSecondTab={disableSecondTab}
            >
                {!teachersLoader &&
                    (showStatistics
                        ? rollup && (
                              <>
                                  <SchoolReportCardStatistics schoolName={locationName} rollup={rollup} />
                                  {renderLocationOverview(true)}
                              </>
                          )
                        : renderLocationOverview(true))}
            </SemesterConnectedPanel>
            {teachersLoader && <LoadingSvg />}
        </div>
    );
};

TeacherOverviewHome.propTypes = {
    setLabelObj: PropTypes.func,
    setSelectedLink: PropTypes.func,
    locationKey: PropTypes.string
};

export default TeacherOverviewHome;
